/**
 * 通用持久化缓存工具
 * 为 Pinia Store 提供统一的三级缓存机制
 */

// ========== 缓存配置接口 ==========
export interface PersistenceConfig {
  /** 存储键名 */
  STORAGE_KEY: string;
  /** 时间戳键名 */
  TIMESTAMP_KEY: string;
  /** 缓存时长（毫秒） */
  CACHE_DURATION: number;
  /** 版本号 */
  VERSION: string;
}

// ========== 缓存数据结构 ==========
export interface CachedData<T> {
  data: T;
  timestamp: number;
  version: string;
}

// ========== 缓存状态信息 ==========
export interface CacheInfo {
  hasCache: boolean;
  isExpired: boolean;
  timestamp: number | null;
  remainingTime: number | null;
}

// ========== 默认配置 ==========
// const DEFAULT_CACHE_DURATION = 30 * 60 * 1000; // 30 分钟
const DEFAULT_CACHE_DURATION = 10 * 1000; // 开发阶段10s

/**
 * 创建持久化配置（带默认值）
 */
export const createPersistenceConfig = (
  key: string,
  duration: number = DEFAULT_CACHE_DURATION,
  version: string = "1.0.0",
): PersistenceConfig => ({
  STORAGE_KEY: `${key}_v${version.replace(/\./g, "_")}`,
  TIMESTAMP_KEY: `${key}_timestamp`,
  CACHE_DURATION: duration,
  VERSION: version,
});

// ========== 核心缓存操作函数 ==========

/**
 * 读取缓存（带过期检查和版本验证）
 */
export const readCache = <T>(config: PersistenceConfig): T | null => {
  try {
    const cachedStr = localStorage.getItem(config.STORAGE_KEY);
    const timestampStr = localStorage.getItem(config.TIMESTAMP_KEY);

    if (!cachedStr || !timestampStr) {
      return null;
    }

    const cached: CachedData<T> = JSON.parse(cachedStr);
    const timestamp = parseInt(timestampStr, 10);

    const now = Date.now();
    const isExpired = now - timestamp > config.CACHE_DURATION;

    if (isExpired) {
      clearCache(config);
      return null;
    }

    if (cached.version !== config.VERSION) {
      clearCache(config);
      return null;
    }

    return cached.data;
  } catch (error) {
    clearCache(config);
    return null;
  }
};

/**
 * 写入缓存（自动添加时间戳和版本）
 */
export const writeCache = <T>(config: PersistenceConfig, data: T): void => {
  try {
    const cachedData: CachedData<T> = {
      data,
      timestamp: Date.now(),
      version: config.VERSION,
    };

    localStorage.setItem(config.STORAGE_KEY, JSON.stringify(cachedData));
    localStorage.setItem(config.TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {}
};

/**
 * 清除缓存
 */
export const clearCache = (config: PersistenceConfig): void => {
  localStorage.removeItem(config.STORAGE_KEY);
  localStorage.removeItem(config.TIMESTAMP_KEY);
};

/**
 * 检查缓存是否可用
 */
export const isCacheAvailable = (config: PersistenceConfig): boolean => {
  try {
    const cachedStr = localStorage.getItem(config.STORAGE_KEY);
    const timestampStr = localStorage.getItem(config.TIMESTAMP_KEY);

    if (!cachedStr || !timestampStr) {
      return false;
    }

    const timestamp = parseInt(timestampStr, 10);
    const now = Date.now();
    return now - timestamp <= config.CACHE_DURATION;
  } catch {
    return false;
  }
};

/**
 * 获取缓存状态信息
 */
export const getCacheInfo = (config: PersistenceConfig): CacheInfo => {
  const timestampStr = localStorage.getItem(config.TIMESTAMP_KEY);

  if (!timestampStr) {
    return {
      hasCache: false,
      isExpired: true,
      timestamp: null,
      remainingTime: null,
    };
  }

  const timestamp = parseInt(timestampStr, 10);
  const now = Date.now();
  const elapsed = now - timestamp;
  const isExpired = elapsed > config.CACHE_DURATION;
  const remainingTime = isExpired
    ? 0
    : Math.max(0, config.CACHE_DURATION - elapsed);

  return {
    hasCache: true,
    isExpired,
    timestamp,
    remainingTime,
  };
};

/**
 * 检查缓存是否即将过期（默认阈值：5 分钟）
 */
export const isCacheExpiring = (
  config: PersistenceConfig,
  threshold: number = 5 * 60 * 1000,
): boolean => {
  const info = getCacheInfo(config);
  if (!info.hasCache || info.isExpired) {
    return false;
  }
  return (info.remainingTime ?? 0) < threshold;
};
